export type Lang = "en" | "fr" | "es" | "pt";

export const t = <T extends Partial<Record<Lang, string>>>(obj: T | undefined, lang: Lang): string =>
  (obj && (obj[lang] || obj.en)) || "";

export const EcosystemContent = {
  hero: {
    eyebrow: {
      en: "◆ The 6-in-1 founder platform",
      fr: "◆ La plateforme 6-en-1 pour fondateurs",
      es: "◆ La plataforma 6-en-1 para fundadores",
      pt: "◆ A plataforma 6-em-1 para fundadores",
    },
    tagline: {
      en: "Ship your business in 4 weeks.",
      fr: "Lancez votre business en 4 semaines.",
      es: "Lanza tu negocio en 4 semanas.",
      pt: "Lance seu negócio em 4 semanas.",
    },
    h1: {
      en: "Built for founders",
      fr: "Conçu pour les fondateurs.",
      es: "Diseñado para fundadores.",
      pt: "Feito para fundadores.",
    },
    sub: {
      en: "Courses, coaching, mentoring, fractional executives, tools, and a founder community — built for Lagos, Lomé, Luanda and the next 10,000 founders.",
      fr: "Cours, coaching, mentorat, dirigeants à temps partagé, outils et communauté de fondateurs — conçus pour Lagos, Lomé, Luanda et les 10 000 prochains fondateurs.",
      es: "Cursos, coaching, mentoría, directivos a tiempo parcial, herramientas y comunidad de fundadores — pensados para Lagos, Lomé, Luanda y los próximos 10 000 fundadores.",
      pt: "Cursos, coaching, mentoria, executivos por tempo parcial, ferramentas e comunidade de fundadores — feitos para Lagos, Lomé, Luanda e os próximos 10.000 fundadores.",
    },
    ctaPrimary: {
      en: "Start free",
      fr: "Commencer gratuitement",
      es: "Empezar gratis",
      pt: "Começar grátis",
    },
    ctaSecondary: {
      en: "Explore the catalog",
      fr: "Explorer le catalogue",
      es: "Explorar el catálogo",
      pt: "Explorar o catálogo",
    },
    ctaTertiary: {
      en: "Talk to an operator",
      fr: "Parler à un opérateur",
      es: "Habla con un operador",
      pt: "Fale com um operador",
    },
    usedBy: {
      en: "Used by founders shipping in Lagos · Lomé · Casablanca · Nairobi · Luanda",
      fr: "Utilisé par des fondateurs qui livrent à Lagos · Lomé · Casablanca · Nairobi · Luanda",
      es: "Usado por fundadores que entregan en Lagos · Lomé · Casablanca · Nairobi · Luanda",
      pt: "Usado por fundadores que entregam em Lagos · Lomé · Casablanca · Nairobi · Luanda",
    },
  },
  pillars_band: {
    eyebrow: {
      en: "─── ECOSYSTEM",
      fr: "─── ÉCOSYSTÈME",
      es: "─── ECOSISTEMA",
      pt: "─── ECOSSISTEMA",
    },
    title: {
      en: "The 6-in-1 ecosystem",
      fr: "L'écosystème 6-en-1",
      es: "El ecosistema 6-en-1",
      pt: "O ecossistema 6-em-1",
    },
    subtitle: {
      en: "Six pillars. One platform. Built for emerging-market founders.",
      fr: "Six piliers. Une seule plateforme. Pensée pour les fondateurs en marchés émergents.",
      es: "Seis pilares. Una plataforma. Pensada para fundadores en mercados emergentes.",
      pt: "Seis pilares. Uma só plataforma. Feita para fundadores em mercados emergentes.",
    },
    hint: {
      en: "Explore each pillar ↓",
      fr: "Explorez chaque pilier ↓",
      es: "Explora cada pilar ↓",
      pt: "Explore cada pilar ↓",
    },
  },
  pillars: [
    {
      number: "01",
      priceFloor: "bootcamp",
      priceSuffix: "",
      icon: "GraduationCap",
      href: "/catalog",
      hue: 258,
      title: { en: "Bootcamps", fr: "Bootcamps", es: "Bootcamps", pt: "Bootcamps" },
      tagline: {
        en: "Live intensives + AI mentor.",
        fr: "Intensifs en direct + mentor IA.",
        es: "Intensivos en vivo + mentor IA.",
        pt: "Intensivos ao vivo + mentor de IA.",
      },
      kpi: {
        en: "45 bootcamps · 92% completion",
        fr: "45 bootcamps · 92% de complétion",
        es: "45 bootcamps · 92% de finalización",
        pt: "45 bootcamps · 92% de conclusão",
      },
    },
    {
      number: "02",
      priceSku: "mentor-session-60",
      priceSuffix: "/session",
      icon: "Compass",
      href: "/services/coaching",
      hue: 35,
      title: { en: "Coaching", fr: "Coaching", es: "Coaching", pt: "Coaching" },
      tagline: {
        en: "1:1 sessions with operators who've shipped.",
        fr: "Sessions 1:1 avec des opérateurs qui ont livré.",
        es: "Sesiones 1:1 con operadores que ya han lanzado.",
        pt: "Sessões 1:1 com operadores que já entregaram.",
      },
      kpi: {
        en: "Vetted operators from Lagos, Dakar & Luanda",
        fr: "Opérateurs sélectionnés de Lagos, Dakar & Luanda",
        es: "Operadores seleccionados de Lagos, Dakar y Luanda",
        pt: "Operadores selecionados de Lagos, Dakar e Luanda",
      },
    },
    {
      number: "03",
      priceSku: "coaching-monthly",
      priceSuffix: "/mo",
      icon: "Users",
      href: "/mentoring",
      hue: 200,
      title: { en: "Mentoring", fr: "Mentorat", es: "Mentoría", pt: "Mentoria" },
      tagline: {
        en: "Long-term guidance from senior founders.",
        fr: "Accompagnement long terme par des fondateurs séniors.",
        es: "Acompañamiento a largo plazo de fundadores senior.",
        pt: "Acompanhamento de longo prazo por fundadores sêniores.",
      },
      kpi: {
        en: "6-month programs · cohort of 8",
        fr: "Programmes de 6 mois · cohorte de 8",
        es: "Programas de 6 meses · cohorte de 8",
        pt: "Programas de 6 meses · turma de 8",
      },
    },
    {
      number: "04",
      priceSku: "fractional-exec",
      priceSuffix: "/mo",
      icon: "Briefcase",
      href: "/fractional",
      hue: 152,
      title: { en: "Fractional Execs", fr: "Dirigeants à temps partagé", es: "Directivos a tiempo parcial", pt: "Executivos por tempo parcial" },
      tagline: {
        en: "Part-time CFOs, CTOs, CMOs on demand.",
        fr: "CFO, CTO, CMO à temps partiel à la demande.",
        es: "CFO, CTO, CMO a tiempo parcial bajo demanda.",
        pt: "CFOs, CTOs, CMOs por tempo parcial sob demanda.",
      },
      kpi: {
        en: "Match in <72h · vetted operators",
        fr: "Mise en relation <72h · opérateurs vérifiés",
        es: "Match en <72h · operadores verificados",
        pt: "Match em <72h · operadores verificados",
      },
    },
    {
      number: "05",
      priceSku: "microservice-base",
      priceSuffix: "",
      icon: "Boxes",
      href: "/services/microservices",
      hue: 322,
      title: { en: "Microservices", fr: "Microservices", es: "Microservicios", pt: "Microsserviços" },
      tagline: {
        en: "Automate and ship faster.",
        fr: "Automatisez et livrez plus vite.",
        es: "Automatiza y entrega más rápido.",
        pt: "Automatize e entregue mais rápido.",
      },
      kpi: {
        en: "Fixed scope · 7-day delivery",
        fr: "Périmètre fixe · livraison en 7 jours",
        es: "Alcance fijo · entrega en 7 días",
        pt: "Escopo fixo · entrega em 7 dias",
      },
    },
    {
      number: "06",
      priceSuffix: "",
      icon: "Users2",
      href: "/community",
      hue: 280,
      title: { en: "Community", fr: "Communauté", es: "Comunidad", pt: "Comunidade" },
      tagline: {
        en: "Peer-to-peer support from founders shipping in your market.",
        fr: "Entraide entre pairs avec des fondateurs qui livrent sur votre marché.",
        es: "Apoyo entre pares de fundadores que ya entregan en tu mercado.",
        pt: "Apoio entre pares de fundadores que já entregam no seu mercado.",
      },
      kpi: {
        en: "Founder lobby · 4 languages",
        fr: "Hall des fondateurs · 4 langues",
        es: "Hall de fundadores · 4 idiomas",
        pt: "Lobby de fundadores · 4 idiomas",
      },
    },
  ],
} as const;
